/**
 * SC-06 — write each rewrite into its shape by id (G-3), in one PowerPoint.run. If the slide's shapes
 * were added, deleted, reordered or edited since the card rendered, nothing is written ("stale").
 * Each shape keeps its formatting (AIVA-9184, see textStyles).
 */

import { logOfficeError } from "../../../../../telemetry";
import { toSlideText } from "../../../services/insertIntoSlide";
import { changedRewrites, type ApplyRewritesResult, type SlideRewrite, type SlideRewriteCard } from "../types";
import { planStyleRestore, STYLE_PROPS, type StyledWord, type TextStyle, wordsOf } from "./textStyles";

/** Past this many words a shape's fonts aren't read: every word is its own proxy in one sync. */
export const MAX_STYLED_WORDS = 400;

/** A shape being written: its rewrite, its text frame, and its text before the write. */
interface WriteTarget {
  rewrite: SlideRewrite;
  frame: PowerPoint.TextFrame;
  before: string;
}

function pptApi(): typeof PowerPoint | undefined {
  return (globalThis as { PowerPoint?: typeof PowerPoint }).PowerPoint;
}

function sameOrder(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

/** A shape inside a group is reached through each group holding it. */
function shapeOf(shapes: PowerPoint.ShapeCollection, rewrite: SlideRewrite): PowerPoint.Shape {
  const [topId = rewrite.id, ...innerIds] = [...(rewrite.groupPath ?? []), rewrite.id];
  return innerIds.reduce((shape, id) => shape.group.shapes.getItem(id), shapes.getItem(topId));
}

/** Null means the property is mixed within the word, so it is left out. */
function styleOf(font: PowerPoint.ShapeFont): TextStyle {
  return {
    bold: font.bold ?? undefined,
    italic: font.italic ?? undefined,
    underline: font.underline ?? undefined,
    color: font.color ?? undefined,
    size: font.size ?? undefined,
    name: font.name ?? undefined,
  };
}

function setStyle(font: PowerPoint.ShapeFont, style: TextStyle): void {
  if (style.bold !== undefined) font.bold = style.bold;
  if (style.italic !== undefined) font.italic = style.italic;
  if (style.underline !== undefined) font.underline = style.underline as PowerPoint.ShapeFont["underline"];
  if (style.color !== undefined) font.color = style.color;
  if (style.size !== undefined) font.size = style.size;
  if (style.name !== undefined) font.name = style.name;
}

/**
 * Each shape's words with their fonts, read before the write. Null for a shape over
 * {@link MAX_STYLED_WORDS}; all null if the read fails — the rewrites are still written, unformatted.
 */
async function readWordStyles(
  context: PowerPoint.RequestContext,
  targets: readonly WriteTarget[],
): Promise<(StyledWord[] | null)[]> {
  try {
    const pending = targets.map(({ frame, before }) => {
      const words = wordsOf(before);
      if (words.length > MAX_STYLED_WORDS) return null;
      return words.map((w) => {
        const font = frame.textRange.getSubstring(w.start, w.length).font;
        font.load(STYLE_PROPS.join(","));
        return { ...w, font };
      });
    });
    await context.sync();
    return pending.map((words) => words?.map(({ font, ...w }) => ({ ...w, style: styleOf(font) })) ?? null);
  } catch (err) {
    logOfficeError(err, "ppt-rephrase-style-read");
    return targets.map(() => null);
  }
}

/** A failure here leaves the rewrites written but unformatted, so it is logged, not thrown. */
async function restoreStyles(
  context: PowerPoint.RequestContext,
  targets: readonly WriteTarget[],
  styles: readonly (StyledWord[] | null)[],
): Promise<void> {
  if (styles.every((s) => s === null)) return;
  try {
    // Plan against the text as PowerPoint now holds it: its paragraph breaks set the offsets.
    targets.forEach(({ frame }) => frame.textRange.load("text"));
    await context.sync();
    const edits = targets.flatMap(({ frame, before }, i) => {
      const words = styles[i];
      if (!words) return [];
      return planStyleRestore(before, words, frame.textRange.text ?? "").map((edit) => ({ frame, edit }));
    });
    if (edits.length === 0) return;
    edits.forEach(({ frame, edit }) => {
      setStyle(frame.textRange.getSubstring(edit.start, edit.length).font, edit.style);
    });
    await context.sync();
  } catch (err) {
    logOfficeError(err, "ppt-rephrase-style-restore");
  }
}

async function applyWithContext(
  context: PowerPoint.RequestContext,
  card: SlideRewriteCard,
): Promise<ApplyRewritesResult> {
  const selected = context.presentation.getSelectedSlides();
  selected.load("items/id");
  await context.sync();
  const slide = selected.items[0];
  if (!slide) return { kind: "stale" };

  const shapes = slide.shapes;
  shapes.load("items/id");
  await context.sync();
  if (!sameOrder(shapes.items.map((s) => s.id), card.shapeIds)) return { kind: "stale" };

  // Unchanged rows are on the card for the user to see; only changed shapes are written.
  const loaded = changedRewrites(card).map((rewrite) => {
    const frame = shapeOf(shapes, rewrite).textFrame;
    frame.textRange.load("text");
    return { rewrite, frame };
  });
  await context.sync();
  const targets: WriteTarget[] = loaded.map((t) => ({ ...t, before: t.frame.textRange.text ?? "" }));
  // A shape whose text changed since the card rendered was edited by the user: don't overwrite it.
  if (targets.some((t) => toSlideText(t.before) !== toSlideText(t.rewrite.original))) return { kind: "stale" };

  const styles = await readWordStyles(context, targets);
  // No shrink-to-fit: it left the slide with uneven font sizes (AIVA-9184).
  targets.forEach(({ frame, rewrite }) => {
    frame.textRange.text = rewrite.rewrite;
  });
  await context.sync();
  await restoreStyles(context, targets, styles);
  return { kind: "applied", count: targets.length };
}

export async function applySlideRewrites(card: SlideRewriteCard): Promise<ApplyRewritesResult> {
  const api = pptApi();
  if (!api) return { kind: "failed" };
  try {
    return await api.run((context) => applyWithContext(context, card));
  } catch (err) {
    logOfficeError(err, "ppt-rephrase-apply");
    return { kind: "failed" };
  }
}
